import { z } from "zod"

export const binauralConfigSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("beat"),
    carrierHertz: z.number().min(40).max(1_500),
    beatHertz: z.number().min(0.5).max(40),
    levelDecibels: z.number().min(-48).max(-6),
    waveform: z.union([z.literal("sine"), z.literal("triangle"), z.literal("square")]),
  }),
  z.object({
    mode: z.literal("spatial"),
    carrierHertz: z.number().min(40).max(1_500),
    levelDecibels: z.number().min(-48).max(-6),
    waveform: z.union([z.literal("sine"), z.literal("triangle"), z.literal("square")]),
    azimuthDegrees: z.number().min(-180).max(180),
    elevationDegrees: z.number().min(-90).max(90),
  }),
])

export type BinauralConfig = z.infer<typeof binauralConfigSchema>

export type BinauralPlan =
  | {
      readonly kind: "beat"
      readonly leftHertz: number
      readonly rightHertz: number
      readonly gain: number
      readonly waveform: OscillatorType
    }
  | {
      readonly kind: "spatial"
      readonly carrierHertz: number
      readonly gain: number
      readonly waveform: OscillatorType
      readonly position: readonly [number, number, number]
    }

function assertNever(value: never): never {
  throw new Error(`Unhandled binaural mode: ${JSON.stringify(value)}`)
}

export function createBinauralPlan(config: BinauralConfig): BinauralPlan {
  const gain = 10 ** (config.levelDecibels / 20)
  switch (config.mode) {
    case "beat":
      return {
        kind: "beat",
        leftHertz: config.carrierHertz,
        rightHertz: config.carrierHertz + config.beatHertz,
        gain,
        waveform: config.waveform,
      }
    case "spatial": {
      const azimuth = (config.azimuthDegrees * Math.PI) / 180
      const elevation = (config.elevationDegrees * Math.PI) / 180
      const horizontal = Math.cos(elevation)
      return {
        kind: "spatial",
        carrierHertz: config.carrierHertz,
        gain,
        waveform: config.waveform,
        position: [
          Math.sin(azimuth) * horizontal,
          Math.sin(elevation),
          -Math.cos(azimuth) * horizontal,
        ],
      }
    }
    default:
      return assertNever(config)
  }
}
